// Env-driven feature flags — the single source of truth for whether each of the
// four heavy analytics features is live in a given deployment.
//
// WHY: shipping all four ON saturated the shared `prodapi` backend (each polls
// it on mount, app-wide). These flags let us deploy prod with them dark and
// enable them one at a time via env var + redeploy.
//
// SEMANTICS: a flag is ON *only* when its env var is exactly the string 'true'.
// Unset, empty, or any other value ⇒ OFF. So a deployment that sets none of
// these ships all four features off by default.
//
// Gating happens in two places, both keyed off this module:
//   - src/components/layout/layout.tsx — an OFF feature's provider/page is never
//     mounted, so it issues ZERO websocket/backend calls (greying the nav is not
//     enough; the polling lives on provider/page mount).
//   - src/components/appbar/* — an OFF feature's nav entry renders greyed and
//     disabled (visible, not clickable).
//
// To turn a feature back on: set its env var to 'true' and rebuild. No other
// change is required.

const isOn = (value: string | undefined): boolean => value === 'true';

export const featureFlags = {
  marketProbabilities: isOn(import.meta.env.REACT_APP_FEATURE_MARKET_PROBABILITIES),
  carryBasis: isOn(import.meta.env.REACT_APP_FEATURE_CARRY_BASIS),
  optionsFlow: isOn(import.meta.env.REACT_APP_FEATURE_OPTIONS_FLOW),
  portfolioPerformance: isOn(import.meta.env.REACT_APP_FEATURE_PORTFOLIO_PERFORMANCE),
  // Sticky-delta risk metric (IM-2369): the backend must compute
  // DELTA(STICKY_DELTA) for it to return data; until that backend release ships
  // it is shown but disabled/greyed so it cannot be selected (selecting it
  // otherwise empties the run). Enable via env var once the backend is live.
  stickyDelta: isOn(import.meta.env.REACT_APP_FEATURE_STICKY_DELTA),
} as const;

// Map each gated nav label (as it appears in the appbar LINKS) to its flag.
// Labels absent from this map are ungated and always enabled.
const NAV_LABEL_FLAGS: Record<string, boolean> = {
  'Market Probabilities': featureFlags.marketProbabilities,
  'Carry & Basis': featureFlags.carryBasis,
  'Options Flow': featureFlags.optionsFlow,
  'Portfolio Performance': featureFlags.portfolioPerformance,
};

// True unless the label maps to an OFF feature flag. Used by the nav to grey +
// disable entries whose feature is dark.
export const isNavLabelEnabled = (label: string): boolean => NAV_LABEL_FLAGS[label] ?? true;

// Independent of the four analytics featureFlags above: gates permission-based
// nav/tab filtering (IM-2594). OFF by default (unset / anything but 'true'), so
// production is unchanged; set to 'true' only in the restricted `tracking` env,
// where it collapses the nav and tab content to the permissions the backend
// grants. Same isOn semantics as the analytics flags.
export const restrictNavByPermission = isOn(import.meta.env.REACT_APP_RESTRICT_NAV_BY_PERMISSION);
